import { FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Flex } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  AuthorizedButton,
  Container,
  DataTable,
  PageTitle,
  SExportButton,
  UserModal,
} from '../../../components';
import { INITIAL_VALUES } from './const';
import styles from './index.module.scss';
import UseHttpService from '../../../services/hook';
import { useAppDispatch, useAppSelector } from '../../../store';
import validationSchema from './validations';
import { getColumns } from './columns';
import { tableActions } from '../../../store/reducers/tableReducer';
import {
  IRole,
  IUserSettingsDataSource,
  IUserSettingsInitialValues,
} from '../../../types';
import { modalTypes } from '../../../assets/enums';
import { downloadFile } from '../../../helpers/File';
import { cleanupAfterSubmit, prepareModalData } from '../../../utils/functions';
import useFetchParameters from '../../../hooks';
import UserSettingsService from '../../../api-clients/UserSettingsService';
import AuthService from '../../../api-clients/AuthService';

const UserSettings: FC = () => {
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);
  const dispatch = useAppDispatch();
  const executeService = UseHttpService();

  // State
  const [selectedItem, setSelectedItem] = useState<
    IUserSettingsDataSource | undefined
  >(undefined);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<IUserSettingsDataSource[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [organizationRoles, setOrganizationRoles] = useState<IRole[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<
    number | undefined
  >(undefined);

  const parameters = useFetchParameters({
    search,
    sort,
    page,
  });
  const userSettingsService = useMemo(
    () => new UserSettingsService(executeService, parameters),
    [executeService, parameters],
  );
  const authService = useMemo(
    () => new AuthService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchOrganizationRoles = async (id: number | undefined) => {
    if (!id) return;
    try {
      const response = await authService.fetchOrganizationRoles(id);
      if (Array.isArray(response)) {
        setOrganizationRoles(response);
      }
    } catch (error) {
      console.error('Error fetching organization roles:', error);
      setOrganizationRoles([]);
    }
  };

  const handleFetchData = async () => {
    const response = await userSettingsService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      setDataSource(response.results);
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selectedItem = await userSettingsService.fetchItemById(id);
    if (selectedItem && 'id' in selectedItem) {
      setSelectedItem(selectedItem);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleFetchRoles = async () => {
    const response = await authService.fetchRoles();
    if (response && Array.isArray(response)) {
      setRoles(response);
    }
  };

  const handleClickDelete = async (id: number) => {
    try {
      await userSettingsService.deleteItemById(id);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IUserSettingsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickExport = async () => {
    const response = await userSettingsService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (values: IUserSettingsInitialValues) => {
    try {
      await userSettingsService.submitForm(values, selectedItem);
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IUserSettingsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  // Columns
  const columns = getColumns(
    handleClickDelete,
    handleOpenEditModal,
    organizations,
    roles,
  );

  // Form Management
  const formik = useFormik<IUserSettingsInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IUserSettingsDataSource,
        IUserSettingsInitialValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [search, sort.key, sort.order, page.number, page.size]);

  useEffect(() => {
    handleFetchRoles();
  }, []);

  useEffect(() => {
    handleFetchOrganizationRoles(selectedOrganization || undefined);
  }, [selectedOrganization]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Kullanıcı Ayarları" />
      <Flex vertical gap="middle">
        <Flex justify="flex-end" gap={5}>
          <SExportButton onClick={handleClickExport} />
          <AuthorizedButton
            model="user"
            action="add"
            className={styles.button}
            onClick={handleOpenModal}
          >
            <PlusOutlined className={styles.buttonIcon} />
            <div className={styles.buttonText}>Yeni Kullanıcı Ekle</div>
          </AuthorizedButton>
        </Flex>
        <DataTable<IUserSettingsDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
        />
      </Flex>
      <UserModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Yeni Kullanıcı Oluşturma"
        subTitle="Yeni Kullanıcı Oluşturma"
        formik={formik}
        roles={organizationRoles}
        organizations={organizations}
        setSelectedOrganization={setSelectedOrganization}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
      />
      <UserModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Kullanıcı Güncelleme"
        subTitle="Kullanıcı Güncelleme"
        formik={formik}
        roles={organizationRoles}
        organizations={organizations}
        setSelectedOrganization={setSelectedOrganization}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
      />
    </Container>
  );
};

export default UserSettings;
